/* eslint-disable @typescript-eslint/no-explicit-any */
//usePortDetails.ts
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AxiosInstance } from 'axios';
import { fetchMTUDetails, fetchPort, fetchPortErrorsAPI, fetchStateDetails, fetchSubscriptionPortDetails } from '../requests/request';
import { useAppSelector } from '@redux/hooks';
import { readCachedFetch, writeCachedFetch } from '@hooks/useCachedFetch';

export function usePortDetails(
  portId: string | null,
  api: AxiosInstance,
  refresh: number
) {
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector(state => state.selection);
  // The last assembled answer for this port paints at once when the user comes back
  // (a view switch, Back); the requests below still run and replace it.
  const cacheKey = portId ? `kontroledge/ports/${portId}?tenantId=${tenantId}` : null;
  const [raw, setRaw] = useState<any>(() => readCachedFetch(cacheKey) ?? null);
  const [loading, setLoading] = useState(() => !readCachedFetch(cacheKey));

  useEffect(() => {
    if (!portId) return;
    let cancelled = false;
    const cached = readCachedFetch(cacheKey);
    if (cached) {
      setRaw(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        // 1️⃣ Core details
        const detailResp = await fetchPort(token, portId, tenantId);
        const details = detailResp.data.data;

        if (cancelled) return;

        // 2️⃣ Kick off all extras in parallel
        const [
          errorsSettled,
          subSettled,
          mtuSettled,
          stateSettled,
        ] = await Promise.allSettled([
          fetchPortErrorsAPI(api, portId, tenantId),
          fetchSubscriptionPortDetails(token, portId, tenantId),
          fetchMTUDetails(token, portId, tenantId),
          fetchStateDetails(token, portId, tenantId),
        ]);

        // 3️⃣ Merge in whichever succeeded
        if (errorsSettled.status === 'fulfilled') {
          details.performanceMetrics = {
            ...details.performanceMetrics,
            errors: errorsSettled.value.data.data.crcErrors
          };
        }
        if (subSettled.status === 'fulfilled') {
          details.subscriptionDetails = {
            ...details.subscriptionDetails,
            ...subSettled.value.data.data
          };
        }
        if (mtuSettled.status === 'fulfilled') {
          details.portConfiguration = {
            ...details.portConfiguration,
            ...mtuSettled.value.data.data
          };
        }
        if (stateSettled.status === 'fulfilled') {
          details.state = {
            ...details.state,
            ...stateSettled.value.data.data
          };
        }

        writeCachedFetch(cacheKey, details);
        if (!cancelled) setRaw(details);
      } catch (e) {
        console.error('Error in usePortDetails:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [portId, api, getAccessTokenSilently, refresh, cacheKey]);

  return { raw, loading } ;
}
